/**
 * Pour Planes.
 *
 * The candidates are **proposed, not taken**. A net silently promoted to a
 * plane is a board that fails in a way nobody looks for, so every net this
 * offers carries why it thinks so — a layer that names it outright, a name that
 * looks like a supply, or simply having more pins than anything else — and
 * nothing is poured until it is ticked.
 *
 * Afterwards the figure that leads is **copper thrown away**: poured copper the
 * clearances cut off from the net, which is joined to nothing and is what a
 * board house queries. Beside it, the pads the pour never actually reached,
 * named — because a pin the netlist calls connected and the copper does not is
 * a bench fault, and the board looks right.
 */

import type { PlaneCandidate, PlaneReport } from '@sircad/core/route/planes.ts';
import { button, buttons, openDialog } from './dialog.ts';

export interface PlaneDialogOptions {
  candidates: PlaneCandidate[];
  /** Layer names, for the picker. */
  layerName(index: number): string;
  /** Plane layers available to pour on. */
  planeLayers: number[];
  formatArea(squareInches: number): string;
  /** Pour the chosen nets. Does not change the board. */
  run(assign: Record<string, number>): PlaneReport;
  /** Land it, after checking. Returns what went wrong, or null. */
  apply(report: PlaneReport): string | null;
  onSave(report: PlaneReport): void;
}

export function showPlanesDialog(opts: PlaneDialogOptions): void {
  openDialog({
    title: 'Pour Planes',
    wide: true,
    build(body, handle) {
      if (opts.candidates.length === 0 || opts.planeLayers.length === 0) {
        const p = document.createElement('p');
        p.className = 'dfm-note';
        p.textContent = opts.planeLayers.length === 0
          ? 'This board has no plane layer to pour on. Add one in Setup ▸ Layers.'
          : 'Nothing on this board looks like a supply net.';
        body.appendChild(p);
        const bar = buttons(body);
        button(bar, 'Close', () => handle.close());
        return;
      }

      const intro = document.createElement('p');
      intro.className = 'dfm-note';
      intro.textContent = 'Nothing is poured until you tick it. A net promoted '
        + 'to a plane by mistake is a board that fails where nobody looks.';
      body.appendChild(intro);

      const rows: { net: string; on: HTMLInputElement; layer: HTMLSelectElement }[] = [];
      const form = document.createElement('div');
      form.className = 'place-form';

      for (const c of opts.candidates) {
        const row = document.createElement('label');
        row.className = 'place-row';

        const on = document.createElement('input');
        on.type = 'checkbox';
        // Only what a layer names outright is certain enough to arrive ticked.
        on.checked = c.why === 'assigned';

        const layer = document.createElement('select');
        for (const i of opts.planeLayers) {
          const o = document.createElement('option');
          o.value = String(i);
          o.textContent = opts.layerName(i);
          layer.appendChild(o);
        }
        if (c.layer !== undefined) layer.value = String(c.layer);

        const text = document.createElement('span');
        text.textContent = `${c.net} — ${c.pins} pins, `
          + (c.why === 'assigned' ? 'named by a layer'
            : c.why === 'named' ? 'looks like a supply'
              : 'the busiest net on the board');

        row.append(on, layer, text);
        form.appendChild(row);
        rows.push({ net: c.net, on, layer });
      }
      body.appendChild(form);

      const out = document.createElement('div');
      out.className = 'place-result';
      body.appendChild(out);

      const bar = buttons(body);
      let found: PlaneReport | null = null;

      const applyBtn = button(bar, 'Apply', () => {
        if (!found) return;
        const trouble = opts.apply(found);
        if (trouble) { out.textContent = trouble; return; }
        handle.close();
      });
      applyBtn.classList.add('primary');
      applyBtn.disabled = true;

      const saveBtn = button(bar, 'Save report…', () => { if (found) opts.onSave(found); });
      saveBtn.disabled = true;

      button(bar, 'Pour', () => {
        const assign: Record<string, number> = {};
        for (const r of rows) if (r.on.checked) assign[r.net] = Number(r.layer.value);
        if (Object.keys(assign).length === 0) {
          out.textContent = 'Tick a net to pour first.';
          return;
        }

        out.textContent = 'Pouring…';
        found = opts.run(assign);
        applyBtn.disabled = found.add.length === 0;
        saveBtn.disabled = false;
        out.textContent = '';

        const stats = document.createElement('dl');
        stats.className = 'density-stats';
        const stat = (label: string, value: string, why?: string): void => {
          const dt = document.createElement('dt');
          dt.textContent = label;
          if (why) dt.title = why;
          const dd = document.createElement('dd');
          dd.textContent = value;
          stats.append(dt, dd);
        };
        // The figure that leads.
        stat('Copper thrown away', opts.formatArea(found.lost),
          'Poured copper the clearances cut off from its net. It is joined to '
          + 'nothing, picks up noise, and gets a job queried.');
        stat('Vias dropped', String(found.vias));
        stat('Nets poured', String(found.nets.length));
        out.appendChild(stats);

        for (const n of found.nets) {
          if (n.unreached.length === 0 && n.orphans === 0) continue;
          const p = document.createElement('p');
          p.className = 'dfm-verdict bad';
          p.textContent = `${n.net}: ${n.unreached.length || n.orphans} pad`
            + `${(n.unreached.length || n.orphans) === 1 ? '' : 's'} the pour never reaches`
            + (n.unreached.length > 0
              ? ` — ${n.unreached.slice(0, 10).map((u) => `${u.ref}.${u.pin}`).join(', ')}`
              : '');
          out.appendChild(p);
        }
      });

      button(bar, 'Close', () => handle.close());
    },
  });
}
